import { useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore.js'
import { registerUser } from '../services/api.js'
import OAuthButtons from '../components/auth/OAuthButtons.jsx'
import '../styles/login.css'

export default function Register() {
  const navigate = useNavigate()
  const { isAuthenticated, login } = useAuthStore()

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const [firstName, ...lastParts] = fullName.trim().split(' ')
      const lastName = lastParts.join(' ') || ''

      const data = await registerUser({ email, password, firstName, lastName })
      const token = data?.token
      if (!token) throw new Error('Réponse d’inscription invalide (token manquant)')

      const user = {
        email: data?.email || email,
        name: `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim() || (email?.split?.('@')[0] || email),
      }

      login(user, token)
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Inscription impossible')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-container">
      {/* Left side - Form */}
      <div className="login-left">
        <div className="login-content">
          <Link to="/" className="back-home">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12 4L6 10L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Retour à l'accueil
          </Link>

          <div className="login-header">
            <h1>Créer un compte</h1>
            <p className="login-subtitle">Rejoignez-nous et automatisez votre prospection</p>
          </div>

          {/* OAuth Buttons */}
          <OAuthButtons />

          <div className="divider">
            <span>ou</span>
          </div>

          {/* Register Box */}
          <div className="login-box">
            <div className="box-header">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2ZM10 4C11.66 4 13 5.34 13 7C13 8.66 11.66 10 10 10C8.34 10 7 8.66 7 7C7 5.34 8.34 4 10 4ZM10 16.2C7.5 16.2 5.29 14.92 4 12.98C4.03 10.99 8 9.9 10 9.9C11.99 9.9 15.97 10.99 16 12.98C14.71 14.92 12.5 16.2 10 16.2Z" fill="currentColor"/>
              </svg>
              <span>Inscription par email</span>
            </div>
            <form onSubmit={onSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="fullName">Nom complet</label>
                <input
                  type="text"
                  id="fullName"
                  placeholder="Jean Dupont"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Adresse email</label>
                <input
                  type="email"
                  id="email"
                  placeholder="vous@exemple.fr"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Mot de passe</label>
                <input
                  type="password"
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
              {error && <p style={{ color: '#ef4444', fontSize: '0.9rem', marginTop: 8 }}>{error}</p>}
              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? 'Inscription…' : 'Créer mon compte'}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </form>
          </div>

          {/* Login CTA */}
          <div className="register-cta">
            <p>Déjà inscrit ?</p>
            <Link to="/login" className="register-link">
              Connectez-vous
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Right side - Video (same as Login) */}
      <div className="login-right">
        <video className="background-video" autoPlay muted loop playsInline>
          <source src="/videos/vague_noir.mp4" type="video/mp4" />
        </video>
        <div className="video-overlay"></div>
        <div className="right-content">
          <div className="brand-logo">
            <img src="/brand/jean-louis-blanc.svg" alt="Jean Louis" />
          </div>
          <h2 className="right-title">Automatisez votre prospection</h2>
          <p className="right-description">
            Surfez sur la vague des entreprises qui génèrent plus de leads grâce à l'IA
          </p>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">200M+</div>
              <div className="stat-label">Profils</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">98%</div>
              <div className="stat-label">Satisfaction</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Automatisation</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

